// src/features/executive/export/excel-columns.ts
// Per-drill column specs for Phase 1F Excel export.
// Each entry returns a { columns, rows } payload consumable by excel-builder.

import type {
  RevenueTrendResponse,
  RevenueTrendPoint,
  AppointmentDetailRow,
  KeyProcedureDetailRow,
  TopProceduresResponse,
  TopProcedureItem,
  TopDoctorsResponse,
  TopDoctorItem,
  TopProductsResponse,
  TopProductItem,
  CategoryMixResponse,
  CategoryMixItem,
  NewPatientDetailRow,
} from 'api/executive/executive.types'

export type ExcelCellValue = string | number | Date | null

export interface ExcelColumnDef<T> {
  header: string          // Column header text (already i18n-resolved by caller)
  key: string             // Unique column key (not shown)
  width: number           // Column width (ExcelJS units, clamp [10, 40])
  numFmt?: string         // Excel number format (e.g. '฿#,##0', '0.0%', 'YYYY-MM-DD')
  alignment?: 'left' | 'right' | 'center'
  getValue: (row: T, index: number) => ExcelCellValue
}

export interface ExcelExportPayload<T> {
  columns: ExcelColumnDef<T>[]
  rows: T[]
}

// -- Revenue --
export function revenueExcelPayload(
  data: RevenueTrendResponse | null,
  labels: { date: string; revenue: string; share: string }
): ExcelExportPayload<RevenueTrendPoint> {
  const points = data?.points ?? []
  const total = data?.totalActual ?? points.reduce((s, p) => s + p.actual, 0)
  return {
    columns: [
      { header: labels.date, key: 'bucket', width: 16, getValue: (p) => p.bucket },
      { header: labels.revenue, key: 'actual', width: 18, numFmt: '฿#,##0', alignment: 'right', getValue: (p) => p.actual },
      { header: labels.share, key: 'share', width: 12, numFmt: '0.0%', alignment: 'right', getValue: (p) => (total > 0 ? p.actual / total : 0) },
    ],
    rows: points,
  }
}

// -- Key Procedures detail --
// Note: KeyProcedureDetailRow.procedure is ProcedureRef { id, name } — uses r.procedure.name
export function keyProceduresExcelPayload(
  rows: KeyProcedureDetailRow[],
  labels: { procedure: string; date: string; cases: string; revenue: string }
): ExcelExportPayload<KeyProcedureDetailRow> {
  return {
    columns: [
      { header: labels.procedure, key: 'procedureName', width: 24, getValue: (r) => r.procedure.name },
      { header: labels.date, key: 'date', width: 14, getValue: (r) => r.date },
      { header: labels.cases, key: 'cases', width: 10, numFmt: '#,##0', alignment: 'right', getValue: (r) => r.cases },
      { header: labels.revenue, key: 'revenue', width: 18, numFmt: '฿#,##0', alignment: 'right', getValue: (r) => r.revenue },
    ],
    rows,
  }
}

// -- Appointments detail --
export function appointmentsExcelPayload(
  rows: AppointmentDetailRow[],
  labels: { date: string; total: string; completed: string; cancelled: string; noShow: string }
): ExcelExportPayload<AppointmentDetailRow> {
  return {
    columns: [
      { header: labels.date, key: 'date', width: 14, getValue: (r) => r.date },
      { header: labels.total, key: 'total', width: 10, numFmt: '#,##0', alignment: 'right', getValue: (r) => r.total },
      { header: labels.completed, key: 'completed', width: 10, numFmt: '#,##0', alignment: 'right', getValue: (r) => r.completed },
      { header: labels.cancelled, key: 'cancelled', width: 10, numFmt: '#,##0', alignment: 'right', getValue: (r) => r.cancelled },
      { header: labels.noShow, key: 'noShow', width: 10, numFmt: '#,##0', alignment: 'right', getValue: (r) => r.noShow },
    ],
    rows,
  }
}

// -- Top Procedures --
export function topProceduresExcelPayload(
  data: TopProceduresResponse | null,
  labels: { rank: string; name: string; cases: string; revenue: string; avg: string; share: string }
): ExcelExportPayload<TopProcedureItem> {
  const items = data?.items ?? []
  return {
    columns: [
      { header: labels.rank, key: 'rank', width: 8, numFmt: '0', alignment: 'right', getValue: (_r, i) => i + 1 },
      { header: labels.name, key: 'name', width: 24, getValue: (r) => r.procedureName },
      { header: labels.cases, key: 'cases', width: 10, numFmt: '#,##0', alignment: 'right', getValue: (r) => r.caseCount },
      { header: labels.revenue, key: 'revenue', width: 18, numFmt: '฿#,##0', alignment: 'right', getValue: (r) => r.revenue },
      { header: labels.avg, key: 'avg', width: 16, numFmt: '฿#,##0', alignment: 'right', getValue: (r) => r.avgPerCase },
      { header: labels.share, key: 'share', width: 12, numFmt: '0.0%', alignment: 'right', getValue: (r) => r.percentShare / 100 },
    ],
    rows: items,
  }
}

// -- Top Doctors --
export function topDoctorsExcelPayload(
  data: TopDoctorsResponse | null,
  labels: { rank: string; name: string; cases: string; revenue: string; avg: string; share: string }
): ExcelExportPayload<TopDoctorItem> {
  const items = data?.items ?? []
  return {
    columns: [
      { header: labels.rank, key: 'rank', width: 8, numFmt: '0', alignment: 'right', getValue: (_r, i) => i + 1 },
      { header: labels.name, key: 'name', width: 24, getValue: (r) => r.doctorName },
      { header: labels.cases, key: 'cases', width: 10, numFmt: '#,##0', alignment: 'right', getValue: (r) => r.caseCount },
      { header: labels.revenue, key: 'revenue', width: 18, numFmt: '฿#,##0', alignment: 'right', getValue: (r) => r.revenue },
      { header: labels.avg, key: 'avg', width: 16, numFmt: '฿#,##0', alignment: 'right', getValue: (r) => r.avgPerCase },
      { header: labels.share, key: 'share', width: 12, numFmt: '0.0%', alignment: 'right', getValue: (r) => r.percentShare / 100 },
    ],
    rows: items,
  }
}

// -- Top Products --
export function topProductsExcelPayload(
  data: TopProductsResponse | null,
  labels: { rank: string; name: string; units: string; revenue: string; avg: string; share: string }
): ExcelExportPayload<TopProductItem> {
  const items = data?.items ?? []
  return {
    columns: [
      { header: labels.rank, key: 'rank', width: 8, numFmt: '0', alignment: 'right', getValue: (_r, i) => i + 1 },
      { header: labels.name, key: 'name', width: 24, getValue: (r) => r.productName },
      { header: labels.units, key: 'units', width: 10, numFmt: '#,##0', alignment: 'right', getValue: (r) => r.unitsSold },
      { header: labels.revenue, key: 'revenue', width: 18, numFmt: '฿#,##0', alignment: 'right', getValue: (r) => r.revenue },
      { header: labels.avg, key: 'avg', width: 16, numFmt: '฿#,##0', alignment: 'right', getValue: (r) => r.avgPerUnit },
      { header: labels.share, key: 'share', width: 12, numFmt: '0.0%', alignment: 'right', getValue: (r) => r.percentShare / 100 },
    ],
    rows: items,
  }
}

// -- Category Mix --
export function categoryMixExcelPayload(
  data: CategoryMixResponse | null,
  labels: { name: string; revenue: string; share: string }
): ExcelExportPayload<CategoryMixItem> {
  const items = data?.categories ?? []
  return {
    columns: [
      { header: labels.name, key: 'name', width: 24, getValue: (r) => r.label },
      { header: labels.revenue, key: 'amount', width: 18, numFmt: '฿#,##0', alignment: 'right', getValue: (r) => r.amount },
      { header: labels.share, key: 'share', width: 12, numFmt: '0.0%', alignment: 'right', getValue: (r) => r.percent / 100 },
    ],
    rows: items,
  }
}

// -- New Patient Trend detail --
export function newPatientTrendExcelPayload(
  rows: NewPatientDetailRow[],
  labels: { date: string; count: string; sampleNames: string }
): ExcelExportPayload<NewPatientDetailRow> {
  return {
    columns: [
      { header: labels.date, key: 'date', width: 14, getValue: (r) => r.date },
      { header: labels.count, key: 'count', width: 10, numFmt: '#,##0', alignment: 'right', getValue: (r) => r.count },
      { header: labels.sampleNames, key: 'names', width: 40, getValue: (r) => r.patientNames.join(', ') },
    ],
    rows,
  }
}
